/**
 * Phase 214 — export a Recharts chart as a PNG image (no extra dependencies).
 * The chart's inline SVG is serialised, rasterised onto a canvas at `scale`×, and framed with a title, subtitle,
 * legend and footer lines so the image stands on its own in a review deck.
 */
const FONT_BODY = "'IBM Plex Sans', Arial, Helvetica, sans-serif";
const FONT_HEAD = "'Space Grotesk', 'IBM Plex Sans', Arial, Helvetica, sans-serif";

const loadImage = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = () => reject(new Error("Could not render the chart image"));
  img.src = src;
});

export function downloadBlobFile(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** The main chart SVG inside `container` — Recharts 3 renders the legend's little icon SVGs (also `.recharts-surface`)
 * BEFORE the chart surface in DOM order, so pick the largest one rather than the first. */
function findChartSvg(container) {
  const all = Array.from(container?.querySelectorAll?.("svg") || []);
  let best = null, bestArea = 0;
  for (const s of all) {
    const r = s.getBoundingClientRect();
    const area = (r.width || Number(s.getAttribute("width")) || 0) * (r.height || Number(s.getAttribute("height")) || 0);
    if (area > bestArea) { best = s; bestArea = area; }
  }
  return best;
}

/** Serialise the main Recharts SVG inside `container` with explicit size + fonts so it rasterises identically. */
function serializeChartSvg(container) {
  const svg = findChartSvg(container);
  if (!svg) throw new Error("The chart is not ready to export yet");
  const rect = svg.getBoundingClientRect();
  const w = Math.max(320, Math.round(rect.width || Number(svg.getAttribute("width")) || 800));
  const h = Math.max(160, Math.round(rect.height || Number(svg.getAttribute("height")) || 300));
  const clone = svg.cloneNode(true);
  clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  clone.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink");
  clone.setAttribute("width", String(w));
  clone.setAttribute("height", String(h));
  if (!clone.getAttribute("viewBox")) clone.setAttribute("viewBox", `0 0 ${w} ${h}`);
  clone.setAttribute("style", `font-family: ${FONT_BODY}; background: #ffffff;`);
  // text nodes rely on inherited fill in the DOM — make it explicit for the isolated SVG image
  clone.querySelectorAll("text").forEach((t) => { if (!t.getAttribute("fill") && !t.style?.fill) t.setAttribute("fill", "#334155"); });
  const xml = new XMLSerializer().serializeToString(clone);
  return { xml, w, h };
}

/**
 * @param {object} o
 * @param {HTMLElement} o.container  element that wraps the Recharts chart (the ResponsiveContainer parent)
 * @param {string}  o.title
 * @param {string}  [o.subtitle]
 * @param {{label:string,color:string,kind?:'bar'|'line'}[]} [o.legend]
 * @param {string[]} [o.footerLines]
 * @param {string}  o.filename   e.g. monthly-collection.png
 * @param {number}  [o.scale=2]
 */
export async function exportChartAsPng({ container, title, subtitle, legend = [], footerLines = [], filename = "chart.png", scale = 2 }) {
  const { xml, w, h } = serializeChartSvg(container);
  const img = await loadImage("data:image/svg+xml;charset=utf-8," + encodeURIComponent(xml));

  const pad = 28;
  const titleH = title ? 30 : 0;
  const subH = subtitle ? 22 : 0;
  const legendH = legend.length ? 26 : 0;
  const footerH = footerLines.length ? footerLines.length * 17 + 10 : 0;
  const totalW = w + pad * 2;
  const totalH = pad + titleH + subH + legendH + 10 + h + footerH + pad;

  const canvas = document.createElement("canvas");
  canvas.width = Math.round(totalW * scale);
  canvas.height = Math.round(totalH * scale);
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas is not supported in this browser");
  ctx.scale(scale, scale);
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, totalW, totalH);
  // brand strip (primary blue) along the top edge
  ctx.fillStyle = "hsl(206,88%,28%)";
  ctx.fillRect(0, 0, totalW, 4);
  ctx.textBaseline = "top";

  let y = pad;
  if (title) {
    ctx.fillStyle = "#0f172a";
    ctx.font = `600 18px ${FONT_HEAD}`;
    ctx.fillText(title, pad, y);
    y += titleH;
  }
  if (subtitle) {
    ctx.fillStyle = "#475569";
    ctx.font = `13px ${FONT_BODY}`;
    ctx.fillText(subtitle, pad, y);
    y += subH;
  }
  if (legend.length) {
    let x = pad;
    ctx.font = `12px ${FONT_BODY}`;
    legend.forEach((l) => {
      ctx.fillStyle = l.color;
      if (l.kind === "line") {
        ctx.fillRect(x, y + 8, 20, 2);
        ctx.beginPath(); ctx.arc(x + 10, y + 9, 3.5, 0, Math.PI * 2); ctx.fill();
        x += 26;
      } else {
        ctx.fillRect(x, y + 3, 12, 12);
        x += 18;
      }
      ctx.fillStyle = "#0f172a";
      ctx.fillText(l.label, x, y + 2);
      x += ctx.measureText(l.label).width + 22;
    });
    y += legendH;
  }
  y += 10;
  ctx.drawImage(img, pad, y, w, h);
  y += h + 10;
  ctx.font = `11px ${FONT_BODY}`;
  footerLines.forEach((line) => {
    ctx.fillStyle = "#64748b";
    ctx.fillText(line, pad, y);
    y += 17;
  });

  const blob = await new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("Could not create the PNG file"))), "image/png"));
  downloadBlobFile(blob, filename);
  return { width: canvas.width, height: canvas.height, bytes: blob.size };
}
